import { apiClient, DEV_USER_ID_STORAGE_KEY } from './api';

const withDevActorQuery = (url: string): string => {
  const devUserId = localStorage.getItem(DEV_USER_ID_STORAGE_KEY) || process.env.REACT_APP_DEV_USER_ID;
  if (!devUserId) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}actor_user_id=${encodeURIComponent(devUserId)}`;
};

export interface User {
  id: number;
  email: string;
  username?: string | null;
  display_name?: string | null;
  status: string;
  is_admin: boolean;
  preferred_ui_language: string;
  preferred_locale?: string | null;
  preferred_text_direction: string;
}

export interface Project {
  id: number;
  title: string;
  description?: string | null;
  status: string;
  story_type?: string | null;
  package_type?: string | null;
  owner_id?: number | null;
  primary_language_code: string;
  locale?: string | null;
  text_direction: string;
}

export interface ProjectCreatePayload {
  title: string;
  owner_id: number;
  description?: string;
  story_type?: string;
  package_type?: string;
  primary_language_code?: string;
  locale?: string;
  text_direction?: string;
  metadata_json?: Record<string, unknown>;
}

export interface ProjectUpdatePayload {
  title?: string;
  description?: string;
  status?: string;
  story_type?: string;
  package_type?: string;
  primary_language_code?: string;
  locale?: string;
  text_direction?: string;
  metadata_json?: Record<string, unknown>;
}

export interface ProjectMembership {
  id: number;
  project_id: number;
  user_id: number;
  role_id?: number | null;
  status: string;
  permission_overrides?: Record<string, string[]> | null;
  user_email: string;
  user_display_name?: string | null;
  role_name?: string | null;
}

export interface AuditEvent {
  id: number;
  project_id?: number | null;
  actor_user_id?: number | null;
  actor_type: string;
  action: string;
  target_type: string;
  target_id?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface Artifact {
  id: number;
  project_id: number;
  artifact_type: string;
  title: string;
  description?: string | null;
  uploader_user_id?: number | null;
  interview_session_id?: number | null;
  file_name?: string | null;
  content_type?: string | null;
  storage_backend?: string | null;
  storage_key?: string | null;
  storage_uri?: string | null;
  external_url?: string | null;
  text_content?: string | null;
  file_size?: number | null;
  checksum?: string | null;
  status: string;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface ArtifactCreatePayload {
  artifact_type: string;
  title: string;
  description?: string;
  uploader_user_id?: number;
  interview_session_id?: number;
  file_name?: string;
  content_type?: string;
  storage_backend?: string;
  storage_key?: string;
  storage_uri?: string;
  external_url?: string;
  text_content?: string;
  file_size?: number;
  checksum?: string;
  status?: string;
  metadata_json?: Record<string, unknown>;
}

export interface ArtifactUpdatePayload {
  title?: string;
  description?: string;
  text_content?: string;
  status?: string;
  metadata_json?: Record<string, unknown>;
}

export interface ProjectInterviewSession {
  id: number;
  interview_id: number;
  interview_title: string;
  interview_type: string;
  interviewer_name: string;
  interviewee_name: string;
  meeting_link?: string | null;
  session_number: number;
  status: string;
  scheduled_at?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  max_duration?: number | null;
  actual_duration?: number | null;
  audio_file_path?: string | null;
  video_file_path?: string | null;
  transcription_text?: string | null;
  transcription_language?: string | null;
  notes?: string | null;
  created_at?: string | null;
}

export interface InterviewSessionCreatePayload {
  title: string;
  interview_type?: 'audio' | 'video';
  interviewer_name?: string;
  interviewee_name: string;
  scheduled_at?: string;
  max_duration?: number;
  notes?: string;
  meeting_link?: string;
}

export interface InterviewStudioZoomSessionPayload {
  meeting_link: string;
  scheduled_at?: string;
  notes?: string;
  status?: 'scheduled' | 'ready' | 'in_progress' | 'completed' | 'canceled';
}

export interface Transcript {
  id: number;
  project_id: number;
  interview_session_id: number;
  source_artifact_id?: number | null;
  created_by_user_id?: number | null;
  text: string;
  language: string;
  confidence?: number | null;
  status: string;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface TranscriptCreatePayload {
  interview_session_id: number;
  text: string;
  language?: string;
  confidence?: number;
  source_artifact_id?: number;
  created_by_user_id?: number;
  status?: string;
  metadata_json?: Record<string, unknown>;
}

export interface TranscriptGeneratePayload {
  interview_session_id: number;
  source_artifact_id: number;
  language?: string;
  created_by_user_id?: number;
}

export interface TranscriptRefinePayload {
  text?: string;
  language?: string;
  instructions?: string;
  created_by_user_id?: number;
}

export interface TranscriptUpdatePayload {
  text: string;
  language?: string;
  confidence?: number;
  status?: string;
  change_note?: string;
  metadata_json?: Record<string, unknown>;
}

export interface TranscriptVersion {
  id: number;
  transcript_id: number;
  version_number: number;
  created_by_user_id?: number | null;
  source_artifact_id?: number | null;
  text: string;
  language: string;
  confidence?: number | null;
  status: string;
  change_note?: string | null;
  provider?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface TranscriptSegment {
  id: number;
  transcript_id: number;
  version_id?: number | null;
  order_index: number;
  speaker_label?: string | null;
  start_seconds?: number | null;
  end_seconds?: number | null;
  text: string;
  language: string;
  confidence?: number | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface StoryOutline {
  id: number;
  project_id: number;
  source_transcript_id: number;
  created_by_user_id?: number | null;
  title: string;
  summary?: string | null;
  outline_json: Record<string, unknown>;
  language: string;
  status: string;
  provider?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface StoryOutlineGeneratePayload {
  source_transcript_id: number;
  title?: string;
  language?: string;
  created_by_user_id?: number;
}

export interface StoryOutlineUpdatePayload {
  title: string;
  summary?: string;
  outline_json: Record<string, unknown>;
  language?: string;
  status?: string;
  change_note?: string;
  metadata_json?: Record<string, unknown>;
}

export interface StoryOutlineVersion {
  id: number;
  outline_id: number;
  version_number: number;
  created_by_user_id?: number | null;
  title: string;
  summary?: string | null;
  outline_json: Record<string, unknown>;
  language: string;
  status: string;
  change_note?: string | null;
  provider?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface StoryChapter {
  id: number;
  project_id: number;
  outline_id: number;
  created_by_user_id?: number | null;
  order_index: number;
  title: string;
  body_text: string;
  language: string;
  status: string;
  provider?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface StoryChapterGeneratePayload {
  outline_id: number;
  language?: string;
  created_by_user_id?: number;
}

export interface StoryChapterUpdatePayload {
  title: string;
  body_text: string;
  order_index?: number;
  language?: string;
  status?: string;
  change_note?: string;
  metadata_json?: Record<string, unknown>;
}

export interface StoryChapterVersion {
  id: number;
  chapter_id: number;
  version_number: number;
  created_by_user_id?: number | null;
  order_index: number;
  title: string;
  body_text: string;
  language: string;
  status: string;
  change_note?: string | null;
  provider?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface WritingStudioSource {
  source_key: string;
  source_type: string;
  id: number;
  title: string;
  language: string;
  text_direction: string;
  status: string;
  text_preview: string;
  text_length: number;
  created_at?: string | null;
  metadata_json?: Record<string, unknown> | null;
}

export interface WritingStudioArtifact {
  artifact_key: string;
  source_type: string;
  id: number;
  title: string;
  story_artifact_type: string;
  language: string;
  text_direction: string;
  status: string;
  body_text?: string | null;
  current_version_number?: number | null;
  editable: boolean;
  created_at?: string | null;
  metadata_json?: Record<string, unknown> | null;
}

export interface WritingStudioVersion {
  version_number: number;
  title: string;
  body_text: string;
  language: string;
  text_direction: string;
  status: string;
  change_note?: string | null;
  source_keys: string[];
  source_references: Record<string, unknown>[];
  agent_metadata?: Record<string, unknown> | null;
  created_by_user_id?: number | null;
  created_at?: string | null;
}

export interface WritingStudioSnapshot {
  sources: WritingStudioSource[];
  story_artifacts: WritingStudioArtifact[];
}

export interface WritingStudioCreateArtifactPayload {
  story_artifact_type: string;
  title: string;
  body_text?: string;
  language?: string;
  text_direction?: string;
  status?: string;
  source_keys?: string[];
  change_note?: string;
}

export interface WritingStudioSaveArtifactPayload {
  title: string;
  body_text: string;
  language?: string;
  text_direction?: string;
  status?: string;
  change_note?: string;
  source_keys?: string[];
  source_references?: Record<string, unknown>[];
  agent_metadata?: Record<string, unknown>;
  manuscript_json?: Record<string, unknown>;
}

export interface WritingStudioSuggestion {
  suggestion_id: string;
  insertion_mode: string;
  target_artifact_id?: string | null;
  target_path?: string | null;
  text: string;
  source_references: Record<string, unknown>[];
  language: string;
  text_direction: string;
}

export interface WritingStudioChatPayload {
  artifact_id?: number;
  title?: string;
  body_text?: string;
  selected_text?: string;
  source_keys?: string[];
  message: string;
  conversation?: Array<{ role: string; content: string }>;
  writer_settings?: Record<string, unknown>;
}

export interface WritingStudioChatResult {
  request_id: string;
  message: string;
  suggestions: WritingStudioSuggestion[];
  suggested_instruction_updates: Array<{ field: string; value: string }>;
  suggested_next_artifact_type?: string | null;
  safety_flags: string[];
  metadata: Record<string, unknown>;
}

export interface WritingStudioGeneratePayload {
  artifact_type?: string;
  requested_title?: string;
  source_keys: string[];
  user_instructions?: string;
  writer_settings?: Record<string, unknown>;
}

export interface WritingStudioGenerateResult {
  request_id: string;
  artifact_type: string;
  title: string;
  body_text: string;
  language: string;
  text_direction: string;
  source_references: Record<string, unknown>[];
  missing_information_questions: string[];
  safety_flags: string[];
  confidence: number;
  metadata: Record<string, unknown>;
}

export interface ReviewComment {
  id: number;
  project_id: number;
  target_type: string;
  target_id: number;
  author_user_id?: number | null;
  author_type: string;
  comment_text: string;
  anchor_text?: string | null;
  anchor_start?: number | null;
  anchor_end?: number | null;
  status: string;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface ReviewCommentFilters {
  target_type?: string;
  target_id?: number;
  status?: string;
}

export interface ReviewCommentStatusCounts {
  total: number;
  open: number;
  resolved: number;
  dismissed: number;
}

export interface Review {
  id: number;
  project_id?: number | null;
  target_type: string;
  target_id: number;
  target_label?: string | null;
  title: string;
  content: string;
  rating?: number | null;
  status: string;
  reviewer_id?: number | null;
  requested_by_user_id?: number | null;
  decided_by_user_id?: number | null;
  decision_summary?: string | null;
  decided_at?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface ReviewDecision {
  id: number;
  review_id: number;
  project_id: number;
  target_type: string;
  target_id: number;
  decision: string;
  decided_by_user_id?: number | null;
  notes?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface Book {
  id: number;
  project_id: number;
  source_artifact_id?: number | null;
  created_by_user_id?: number | null;
  approved_by_user_id?: number | null;
  title: string;
  subtitle?: string | null;
  author_name?: string | null;
  language: string;
  text_direction: string;
  status: string;
  book_type?: string;
  manuscript_json: Record<string, unknown>;
  design_settings_json: Record<string, unknown>;
  cover_json?: Record<string, unknown> | null;
  approved_at?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface BookCreatePayload {
  source_artifact_id?: number;
  created_by_user_id?: number;
  title: string;
  subtitle?: string;
  author_name?: string;
  language?: string;
  text_direction?: string;
  book_type?: string;
  manuscript_json?: Record<string, unknown>;
  design_settings_json?: Record<string, unknown>;
  cover_json?: Record<string, unknown>;
  metadata_json?: Record<string, unknown>;
}

export interface BookUpdatePayload {
  source_artifact_id?: number;
  title?: string;
  subtitle?: string;
  author_name?: string;
  language?: string;
  text_direction?: string;
  status?: string;
  book_type?: string;
  manuscript_json?: Record<string, unknown>;
  design_settings_json?: Record<string, unknown>;
  cover_json?: Record<string, unknown>;
  metadata_json?: Record<string, unknown>;
}

export interface BookVersion {
  id: number;
  book_id: number;
  version_number: number;
  created_by_user_id?: number | null;
  title: string;
  subtitle?: string | null;
  author_name?: string | null;
  language: string;
  text_direction: string;
  status: string;
  manuscript_json: Record<string, unknown>;
  design_settings_json: Record<string, unknown>;
  cover_json?: Record<string, unknown> | null;
  change_note?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface BookVersionCreatePayload {
  created_by_user_id?: number;
  change_note?: string;
  metadata_json?: Record<string, unknown>;
}

export interface BookDesignTemplate {
  id: number;
  created_by_user_id?: number | null;
  name: string;
  description?: string | null;
  book_type: string;
  language?: string | null;
  text_direction?: string | null;
  design_settings_json: Record<string, unknown>;
  is_active: boolean;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BookDesignTemplateCreatePayload {
  name: string;
  description?: string;
  book_type?: string;
  language?: string;
  text_direction?: 'ltr' | 'rtl';
  design_settings_json: Record<string, unknown>;
  metadata_json?: Record<string, unknown>;
}

export interface BookDesignTemplateUpdatePayload {
  name?: string;
  description?: string;
  book_type?: string;
  language?: string;
  text_direction?: 'ltr' | 'rtl';
  design_settings_json?: Record<string, unknown>;
  is_active?: boolean;
  metadata_json?: Record<string, unknown>;
}

export interface BookExport {
  id: number;
  project_id: number;
  book_id: number;
  version_id?: number | null;
  requested_by_user_id?: number | null;
  export_type: string;
  status: string;
  print_profile_json?: Record<string, unknown> | null;
  preflight_status: string;
  preflight_report_json?: Record<string, unknown> | null;
  storage_backend?: string | null;
  storage_key?: string | null;
  storage_uri?: string | null;
  file_size?: number | null;
  checksum?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface BookExportCreatePayload {
  requested_by_user_id?: number;
  version_id?: number;
  export_type?: string;
  print_profile_json?: Record<string, unknown>;
  metadata_json?: Record<string, unknown>;
}

export interface BookExportUpdatePayload {
  status?: string;
  preflight_status?: string;
  preflight_report_json?: Record<string, unknown>;
  storage_backend?: string;
  storage_key?: string;
  storage_uri?: string;
  file_size?: number;
  checksum?: string;
  metadata_json?: Record<string, unknown>;
}

export interface BookExportPreviewPage {
  number: number;
  kind: string;
  label: string;
  source: string;
}

export interface BookExportPreviewManifest {
  export_id: number;
  project_id: number;
  book_id: number;
  available: boolean;
  status: string;
  preflight_status: string;
  renderer?: string | null;
  content_type?: string | null;
  file_name?: string | null;
  preview_url: string;
  download_url: string;
  pages: BookExportPreviewPage[];
  page_count: number;
  source: string;
  layout_manifest?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface PublishingRequest {
  id: number;
  project_id: number;
  book_id: number;
  export_id: number;
  requested_by_user_id?: number | null;
  submitted_by_user_id?: number | null;
  provider?: string | null;
  status: string;
  marketplace?: string | null;
  isbn?: string | null;
  asin?: string | null;
  external_reference?: string | null;
  reviewer_notes?: string | null;
  submitted_at?: string | null;
  notes?: string | null;
  metadata_json?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface PublishingRequestDashboardItem extends PublishingRequest {
  project_title: string;
  book_title: string;
  export_type: string;
  export_status: string;
  preflight_status: string;
}

export interface PublishingRequestCreatePayload {
  export_id: number;
  requested_by_user_id?: number;
  provider?: string;
  notes?: string;
  metadata_json?: Record<string, unknown>;
}

export interface PublishingRequestUpdatePayload {
  status?: string;
  submitted_by_user_id?: number;
  provider?: string;
  marketplace?: string;
  isbn?: string;
  asin?: string;
  external_reference?: string;
  reviewer_notes?: string;
  submitted_at?: string;
  notes?: string;
  metadata_json?: Record<string, unknown>;
}

export interface ReviewCreatePayload {
  target_type: string;
  target_id: number;
  reviewer_id?: number;
  requested_by_user_id?: number;
  title?: string;
  content?: string;
  metadata_json?: Record<string, unknown>;
}

export interface ReviewDecisionCreatePayload {
  decision: 'approved' | 'changes_requested' | 'rejected';
  notes?: string;
  decided_by_user_id?: number;
  metadata_json?: Record<string, unknown>;
}

export interface ReviewCommentCreatePayload {
  target_type: string;
  target_id: number;
  comment_text: string;
  author_user_id?: number;
  author_type?: 'user' | 'ai';
  anchor_text?: string;
  anchor_start?: number;
  anchor_end?: number;
  status?: string;
  metadata_json?: Record<string, unknown>;
}

export interface ReviewCommentUpdatePayload {
  comment_text?: string;
  status?: string;
  anchor_text?: string;
  anchor_start?: number;
  anchor_end?: number;
  metadata_json?: Record<string, unknown>;
}

export interface UserCreatePayload {
  email: string;
  username?: string;
  password?: string;
  display_name?: string;
  phone_number?: string;
  is_admin?: boolean;
  preferred_ui_language?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export const platformApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/platform/auth/login', { email, password });
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/api/platform/users/me');
    return response.data;
  },

  async updateCurrentUserPreferences(preferredUiLanguage: string): Promise<User> {
    const response = await apiClient.patch<User>('/api/platform/users/me/preferences', {
      preferred_ui_language: preferredUiLanguage,
    });
    return response.data;
  },

  async listUsers(): Promise<User[]> {
    const response = await apiClient.get<User[]>('/api/platform/users');
    return response.data;
  },

  async createUser(payload: UserCreatePayload): Promise<User> {
    const response = await apiClient.post<User>('/api/platform/users', payload);
    return response.data;
  },

  async updateUserStatus(userId: number, status: 'active' | 'deactivated'): Promise<User> {
    const response = await apiClient.patch<User>(`/api/platform/users/${userId}/status`, { status });
    return response.data;
  },

  async listProjects(): Promise<Project[]> {
    const response = await apiClient.get<Project[]>('/api/platform/projects');
    return response.data;
  },

  async getProject(projectId: number): Promise<Project> {
    const response = await apiClient.get<Project>(`/api/platform/projects/${projectId}`);
    return response.data;
  },

  async createProject(payload: ProjectCreatePayload): Promise<Project> {
    const response = await apiClient.post<Project>('/api/platform/projects', payload);
    return response.data;
  },

  async updateProject(projectId: number, payload: ProjectUpdatePayload): Promise<Project> {
    const response = await apiClient.patch<Project>(`/api/platform/projects/${projectId}`, payload);
    return response.data;
  },

  async listProjectMemberships(projectId: number): Promise<ProjectMembership[]> {
    const response = await apiClient.get<ProjectMembership[]>(
      `/api/platform/projects/${projectId}/memberships`
    );
    return response.data;
  },

  async assignProjectMember(projectId: number, userId: number, roleName: string): Promise<ProjectMembership> {
    const response = await apiClient.post<ProjectMembership>(
      `/api/platform/projects/${projectId}/memberships`,
      { user_id: userId, role_name: roleName }
    );
    return response.data;
  },

  async unassignProjectMember(projectId: number, membershipId: number): Promise<ProjectMembership> {
    const response = await apiClient.delete<ProjectMembership>(
      `/api/platform/projects/${projectId}/memberships/${membershipId}`
    );
    return response.data;
  },

  async listProjectAuditEvents(projectId: number): Promise<AuditEvent[]> {
    const response = await apiClient.get<AuditEvent[]>(
      `/api/platform/projects/${projectId}/audit-events`
    );
    return response.data;
  },

  async listProjectArtifacts(projectId: number): Promise<Artifact[]> {
    const response = await apiClient.get<Artifact[]>(
      `/api/platform/projects/${projectId}/artifacts`
    );
    return response.data;
  },

  async createProjectArtifact(projectId: number, payload: ArtifactCreatePayload): Promise<Artifact> {
    const response = await apiClient.post<Artifact>(
      `/api/platform/projects/${projectId}/artifacts`,
      payload
    );
    return response.data;
  },

  async updateProjectArtifact(
    projectId: number,
    artifactId: number,
    payload: ArtifactUpdatePayload
  ): Promise<Artifact> {
    const response = await apiClient.patch<Artifact>(
      `/api/platform/projects/${projectId}/artifacts/${artifactId}`,
      payload
    );
    return response.data;
  },

  async uploadProjectArtifact(
    projectId: number,
    payload: {
      artifact_type: string;
      file: File;
      title?: string;
      description?: string;
      uploader_user_id?: number;
      interview_session_id?: number;
    }
  ): Promise<Artifact> {
    const formData = new FormData();
    formData.append('artifact_type', payload.artifact_type);
    formData.append('file', payload.file);
    if (payload.title) {
      formData.append('title', payload.title);
    }
    if (payload.description) {
      formData.append('description', payload.description);
    }
    if (payload.uploader_user_id) {
      formData.append('uploader_user_id', payload.uploader_user_id.toString());
    }
    if (payload.interview_session_id) {
      formData.append('interview_session_id', payload.interview_session_id.toString());
    }
    const response = await apiClient.post<Artifact>(
      `/api/platform/projects/${projectId}/artifacts/upload`,
      formData
    );
    return response.data;
  },

  async uploadProjectStoryDocument(
    projectId: number,
    payload: {
      file: File;
      title?: string;
      description?: string;
      text_content?: string;
      uploader_user_id?: number;
    }
  ): Promise<Artifact> {
    const formData = new FormData();
    formData.append('file', payload.file);
    if (payload.title) {
      formData.append('title', payload.title);
    }
    if (payload.description) {
      formData.append('description', payload.description);
    }
    if (payload.text_content) {
      formData.append('text_content', payload.text_content);
    }
    if (payload.uploader_user_id) {
      formData.append('uploader_user_id', payload.uploader_user_id.toString());
    }
    const response = await apiClient.post<Artifact>(
      `/api/platform/projects/${projectId}/story-documents/upload`,
      formData
    );
    return response.data;
  },

  async sendStoryDocumentToDesign(projectId: number, artifactId: number): Promise<Artifact> {
    const response = await apiClient.post<Artifact>(
      `/api/platform/projects/${projectId}/story-documents/${artifactId}/send-to-design`
    );
    return response.data;
  },

  async sendStoryDocumentToPublishing(projectId: number, artifactId: number): Promise<Artifact> {
    const response = await apiClient.post<Artifact>(
      `/api/platform/projects/${projectId}/story-documents/${artifactId}/send-to-publishing`
    );
    return response.data;
  },

  async listProjectBooks(projectId: number): Promise<Book[]> {
    const response = await apiClient.get<Book[]>(`/api/platform/projects/${projectId}/books`);
    return response.data;
  },

  async createProjectBook(projectId: number, payload: BookCreatePayload): Promise<Book> {
    const response = await apiClient.post<Book>(`/api/platform/projects/${projectId}/books`, payload);
    return response.data;
  },

  async getProjectBook(projectId: number, bookId: number): Promise<Book> {
    const response = await apiClient.get<Book>(`/api/platform/projects/${projectId}/books/${bookId}`);
    return response.data;
  },

  async updateProjectBook(projectId: number, bookId: number, payload: BookUpdatePayload): Promise<Book> {
    const response = await apiClient.patch<Book>(
      `/api/platform/projects/${projectId}/books/${bookId}`,
      payload
    );
    return response.data;
  },

  async deleteProjectBook(projectId: number, bookId: number): Promise<void> {
    await apiClient.delete(`/api/platform/projects/${projectId}/books/${bookId}`);
  },

  async createProjectBookVersion(
    projectId: number,
    bookId: number,
    payload: BookVersionCreatePayload
  ): Promise<BookVersion> {
    const response = await apiClient.post<BookVersion>(
      `/api/platform/projects/${projectId}/books/${bookId}/versions`,
      payload
    );
    return response.data;
  },

  async listProjectBookVersions(projectId: number, bookId: number): Promise<BookVersion[]> {
    const response = await apiClient.get<BookVersion[]>(
      `/api/platform/projects/${projectId}/books/${bookId}/versions`
    );
    return response.data;
  },

  async listBookDesignTemplates(): Promise<BookDesignTemplate[]> {
    const response = await apiClient.get<BookDesignTemplate[]>('/api/platform/design-templates');
    return response.data;
  },

  async createBookDesignTemplate(payload: BookDesignTemplateCreatePayload): Promise<BookDesignTemplate> {
    const response = await apiClient.post<BookDesignTemplate>('/api/platform/design-templates', payload);
    return response.data;
  },

  async updateBookDesignTemplate(
    templateId: number,
    payload: BookDesignTemplateUpdatePayload
  ): Promise<BookDesignTemplate> {
    const response = await apiClient.patch<BookDesignTemplate>(
      `/api/platform/design-templates/${templateId}`,
      payload
    );
    return response.data;
  },

  async deleteBookDesignTemplate(templateId: number): Promise<BookDesignTemplate> {
    const response = await apiClient.delete<BookDesignTemplate>(`/api/platform/design-templates/${templateId}`);
    return response.data;
  },

  async approveProjectBook(projectId: number, bookId: number): Promise<Book> {
    const response = await apiClient.post<Book>(
      `/api/platform/projects/${projectId}/books/${bookId}/approve`
    );
    return response.data;
  },

  async createProjectBookExport(
    projectId: number,
    bookId: number,
    payload: BookExportCreatePayload
  ): Promise<BookExport> {
    const response = await apiClient.post<BookExport>(
      `/api/platform/projects/${projectId}/books/${bookId}/exports`,
      payload
    );
    return response.data;
  },

  async createProjectBookPreviewExport(
    projectId: number,
    bookId: number,
    payload: BookExportCreatePayload
  ): Promise<BookExport> {
    const response = await apiClient.post<BookExport>(
      `/api/platform/projects/${projectId}/books/${bookId}/preview-export`,
      payload
    );
    return response.data;
  },

  async listProjectBookExports(projectId: number, bookId: number): Promise<BookExport[]> {
    const response = await apiClient.get<BookExport[]>(
      `/api/platform/projects/${projectId}/books/${bookId}/exports`
    );
    return response.data;
  },

  getProjectBookExportDownloadUrl(projectId: number, bookId: number, exportId: number): string {
    return withDevActorQuery(`/api/platform/projects/${projectId}/books/${bookId}/exports/${exportId}/download`);
  },

  getProjectBookExportPreviewUrl(projectId: number, bookId: number, exportId: number): string {
    return withDevActorQuery(`/api/platform/projects/${projectId}/books/${bookId}/exports/${exportId}/preview`);
  },

  getProjectBookPreviewHtmlUrl(projectId: number, bookId: number): string {
    return withDevActorQuery(`/api/platform/projects/${projectId}/books/${bookId}/preview-html`);
  },

  getVivliostyleViewerUrl(sourceUrl: string): string {
    return `/api/platform/vivliostyle-viewer/index.html#src=${sourceUrl}&bookMode=false&renderAllPages=true&spread=true`;
  },

  async getProjectBookExportPreviewManifest(
    projectId: number,
    bookId: number,
    exportId: number
  ): Promise<BookExportPreviewManifest> {
    const response = await apiClient.get<BookExportPreviewManifest>(
      `/api/platform/projects/${projectId}/books/${bookId}/exports/${exportId}/preview-manifest`
    );
    return response.data;
  },

  async preflightProjectBookExport(projectId: number, bookId: number, exportId: number): Promise<BookExport> {
    const response = await apiClient.post<BookExport>(
      `/api/platform/projects/${projectId}/books/${bookId}/exports/${exportId}/preflight`
    );
    return response.data;
  },

  async updateProjectBookExport(
    projectId: number,
    bookId: number,
    exportId: number,
    payload: BookExportUpdatePayload
  ): Promise<BookExport> {
    const response = await apiClient.patch<BookExport>(
      `/api/platform/projects/${projectId}/books/${bookId}/exports/${exportId}`,
      payload
    );
    return response.data;
  },

  async createProjectPublishingRequest(
    projectId: number,
    bookId: number,
    payload: PublishingRequestCreatePayload
  ): Promise<PublishingRequest> {
    const response = await apiClient.post<PublishingRequest>(
      `/api/platform/projects/${projectId}/books/${bookId}/publishing-requests`,
      payload
    );
    return response.data;
  },

  async listProjectPublishingRequests(projectId: number, bookId: number): Promise<PublishingRequest[]> {
    const response = await apiClient.get<PublishingRequest[]>(
      `/api/platform/projects/${projectId}/books/${bookId}/publishing-requests`
    );
    return response.data;
  },

  async listPublishingRequests(filters: { status?: string; provider?: string } = {}): Promise<PublishingRequestDashboardItem[]> {
    const params = new URLSearchParams();
    if (filters.status) {
      params.set('status', filters.status);
    }
    if (filters.provider) {
      params.set('provider', filters.provider);
    }
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<PublishingRequestDashboardItem[]>(`/api/platform/publishing-requests${suffix}`);
    return response.data;
  },

  async updateProjectPublishingRequest(
    projectId: number,
    bookId: number,
    publishingRequestId: number,
    payload: PublishingRequestUpdatePayload
  ): Promise<PublishingRequest> {
    const response = await apiClient.patch<PublishingRequest>(
      `/api/platform/projects/${projectId}/books/${bookId}/publishing-requests/${publishingRequestId}`,
      payload
    );
    return response.data;
  },

  getProjectArtifactDownloadUrl(projectId: number, artifactId: number): string {
    return withDevActorQuery(`/api/platform/projects/${projectId}/artifacts/${artifactId}/download`);
  },

  async listProjectInterviewSessions(projectId: number): Promise<ProjectInterviewSession[]> {
    const response = await apiClient.get<ProjectInterviewSession[]>(
      `/api/platform/projects/${projectId}/interview-sessions`
    );
    return response.data;
  },

  async createProjectInterviewSession(
    projectId: number,
    payload: InterviewSessionCreatePayload
  ): Promise<ProjectInterviewSession> {
    const response = await apiClient.post<ProjectInterviewSession>(
      `/api/platform/projects/${projectId}/interview-sessions`,
      payload
    );
    return response.data;
  },

  async configureInterviewStudioZoomSession(
    projectId: number,
    sessionId: number,
    payload: InterviewStudioZoomSessionPayload
  ): Promise<ProjectInterviewSession> {
    const response = await apiClient.patch<ProjectInterviewSession>(
      `/api/platform/projects/${projectId}/interview-studio/sessions/${sessionId}/zoom`,
      payload
    );
    return response.data;
  },

  interviewStudioVoiceAgentStreamUrl(projectId: number, sessionId: number): string {
    const path = `/api/platform/projects/${projectId}/interview-studio/sessions/${sessionId}/voice-agent/stream`;
    const base = process.env.REACT_APP_API_BASE_URL ?? '';
    const origin = base
      ? base.replace(/^http/, 'ws')
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
    const devUserId =
      localStorage.getItem(DEV_USER_ID_STORAGE_KEY) || process.env.REACT_APP_DEV_USER_ID;
    const query = devUserId ? `?actor_user_id=${encodeURIComponent(devUserId)}` : '';
    return `${origin}${path}${query}`;
  },

  async listProjectTranscripts(projectId: number): Promise<Transcript[]> {
    const response = await apiClient.get<Transcript[]>(
      `/api/platform/projects/${projectId}/transcripts`
    );
    return response.data;
  },

  async createProjectTranscript(projectId: number, payload: TranscriptCreatePayload): Promise<Transcript> {
    const response = await apiClient.post<Transcript>(
      `/api/platform/projects/${projectId}/transcripts`,
      payload
    );
    return response.data;
  },

  async generateProjectTranscript(projectId: number, payload: TranscriptGeneratePayload): Promise<Transcript> {
    const response = await apiClient.post<Transcript>(
      `/api/platform/projects/${projectId}/transcripts/generate`,
      payload
    );
    return response.data;
  },

  async refineProjectTranscript(
    projectId: number,
    transcriptId: number,
    payload: TranscriptRefinePayload
  ): Promise<Transcript> {
    const response = await apiClient.post<Transcript>(
      `/api/platform/projects/${projectId}/transcripts/${transcriptId}/refine`,
      payload
    );
    return response.data;
  },

  async updateProjectTranscript(
    projectId: number,
    transcriptId: number,
    payload: TranscriptUpdatePayload
  ): Promise<Transcript> {
    const response = await apiClient.patch<Transcript>(
      `/api/platform/projects/${projectId}/transcripts/${transcriptId}`,
      payload
    );
    return response.data;
  },

  async approveProjectTranscript(projectId: number, transcriptId: number): Promise<Transcript> {
    const response = await apiClient.post<Transcript>(
      `/api/platform/projects/${projectId}/transcripts/${transcriptId}/approve`
    );
    return response.data;
  },

  async deleteProjectTranscript(projectId: number, transcriptId: number): Promise<void> {
    await apiClient.delete(
      `/api/platform/projects/${projectId}/transcripts/${transcriptId}`
    );
  },

  async listProjectTranscriptVersions(projectId: number, transcriptId: number): Promise<TranscriptVersion[]> {
    const response = await apiClient.get<TranscriptVersion[]>(
      `/api/platform/projects/${projectId}/transcripts/${transcriptId}/versions`
    );
    return response.data;
  },

  async listProjectTranscriptSegments(projectId: number, transcriptId: number): Promise<TranscriptSegment[]> {
    const response = await apiClient.get<TranscriptSegment[]>(
      `/api/platform/projects/${projectId}/transcripts/${transcriptId}/segments`
    );
    return response.data;
  },

  async listProjectStoryOutlines(projectId: number): Promise<StoryOutline[]> {
    const response = await apiClient.get<StoryOutline[]>(
      `/api/platform/projects/${projectId}/story-outlines`
    );
    return response.data;
  },

  async generateProjectStoryOutline(
    projectId: number,
    payload: StoryOutlineGeneratePayload
  ): Promise<StoryOutline> {
    const response = await apiClient.post<StoryOutline>(
      `/api/platform/projects/${projectId}/story-outlines/generate`,
      payload
    );
    return response.data;
  },

  async updateProjectStoryOutline(
    projectId: number,
    outlineId: number,
    payload: StoryOutlineUpdatePayload
  ): Promise<StoryOutline> {
    const response = await apiClient.patch<StoryOutline>(
      `/api/platform/projects/${projectId}/story-outlines/${outlineId}`,
      payload
    );
    return response.data;
  },

  async approveProjectStoryOutline(projectId: number, outlineId: number): Promise<StoryOutline> {
    const response = await apiClient.post<StoryOutline>(
      `/api/platform/projects/${projectId}/story-outlines/${outlineId}/approve`
    );
    return response.data;
  },

  async listProjectStoryOutlineVersions(projectId: number, outlineId: number): Promise<StoryOutlineVersion[]> {
    const response = await apiClient.get<StoryOutlineVersion[]>(
      `/api/platform/projects/${projectId}/story-outlines/${outlineId}/versions`
    );
    return response.data;
  },

  async listProjectStoryChapters(projectId: number): Promise<StoryChapter[]> {
    const response = await apiClient.get<StoryChapter[]>(
      `/api/platform/projects/${projectId}/story-chapters`
    );
    return response.data;
  },

  async generateProjectStoryChapters(
    projectId: number,
    payload: StoryChapterGeneratePayload
  ): Promise<StoryChapter[]> {
    const response = await apiClient.post<StoryChapter[]>(
      `/api/platform/projects/${projectId}/story-chapters/generate`,
      payload
    );
    return response.data;
  },

  async updateProjectStoryChapter(
    projectId: number,
    chapterId: number,
    payload: StoryChapterUpdatePayload
  ): Promise<StoryChapter> {
    const response = await apiClient.patch<StoryChapter>(
      `/api/platform/projects/${projectId}/story-chapters/${chapterId}`,
      payload
    );
    return response.data;
  },

  async approveProjectStoryChapter(projectId: number, chapterId: number): Promise<StoryChapter> {
    const response = await apiClient.post<StoryChapter>(
      `/api/platform/projects/${projectId}/story-chapters/${chapterId}/approve`
    );
    return response.data;
  },

  async listProjectStoryChapterVersions(projectId: number, chapterId: number): Promise<StoryChapterVersion[]> {
    const response = await apiClient.get<StoryChapterVersion[]>(
      `/api/platform/projects/${projectId}/story-chapters/${chapterId}/versions`
    );
    return response.data;
  },

  async getWritingStudioSnapshot(projectId: number): Promise<WritingStudioSnapshot> {
    const response = await apiClient.get<WritingStudioSnapshot>(
      `/api/platform/projects/${projectId}/writing-studio`
    );
    return response.data;
  },

  async listWritingStudioSources(projectId: number): Promise<WritingStudioSource[]> {
    const response = await apiClient.get<WritingStudioSource[]>(
      `/api/platform/projects/${projectId}/writing-studio/sources`
    );
    return response.data;
  },

  async listWritingStudioStoryArtifacts(projectId: number): Promise<WritingStudioArtifact[]> {
    const response = await apiClient.get<WritingStudioArtifact[]>(
      `/api/platform/projects/${projectId}/writing-studio/story-artifacts`
    );
    return response.data;
  },

  async createWritingStudioArtifact(
    projectId: number,
    payload: WritingStudioCreateArtifactPayload
  ): Promise<WritingStudioArtifact> {
    const response = await apiClient.post<WritingStudioArtifact>(
      `/api/platform/projects/${projectId}/writing-studio/story-artifacts`,
      payload
    );
    return response.data;
  },

  async saveWritingStudioArtifactVersion(
    projectId: number,
    artifactId: number,
    payload: WritingStudioSaveArtifactPayload
  ): Promise<WritingStudioArtifact> {
    const response = await apiClient.post<WritingStudioArtifact>(
      `/api/platform/projects/${projectId}/writing-studio/story-artifacts/${artifactId}/versions`,
      payload
    );
    return response.data;
  },

  async listWritingStudioArtifactVersions(projectId: number, artifactId: number): Promise<WritingStudioVersion[]> {
    const response = await apiClient.get<WritingStudioVersion[]>(
      `/api/platform/projects/${projectId}/writing-studio/story-artifacts/${artifactId}/versions`
    );
    return response.data;
  },

  async chatWithWritingStudio(projectId: number, payload: WritingStudioChatPayload): Promise<WritingStudioChatResult> {
    const response = await apiClient.post<WritingStudioChatResult>(
      `/api/platform/projects/${projectId}/writing-studio/chat`,
      payload
    );
    return response.data;
  },

  async generateWritingStudioArtifact(
    projectId: number,
    payload: WritingStudioGeneratePayload
  ): Promise<WritingStudioGenerateResult> {
    const response = await apiClient.post<WritingStudioGenerateResult>(
      `/api/platform/projects/${projectId}/writing-studio/generate`,
      payload
    );
    return response.data;
  },

  async listProjectReviews(projectId: number): Promise<Review[]> {
    const response = await apiClient.get<Review[]>(`/api/platform/projects/${projectId}/reviews`);
    return response.data;
  },

  async createProjectReview(projectId: number, payload: ReviewCreatePayload): Promise<Review> {
    const response = await apiClient.post<Review>(
      `/api/platform/projects/${projectId}/reviews`,
      payload
    );
    return response.data;
  },

  async startProjectReview(projectId: number, reviewId: number): Promise<Review> {
    const response = await apiClient.post<Review>(
      `/api/platform/projects/${projectId}/reviews/${reviewId}/start`
    );
    return response.data;
  },

  async decideProjectReview(
    projectId: number,
    reviewId: number,
    payload: ReviewDecisionCreatePayload
  ): Promise<Review> {
    const response = await apiClient.post<Review>(
      `/api/platform/projects/${projectId}/reviews/${reviewId}/decisions`,
      payload
    );
    return response.data;
  },

  async listProjectReviewDecisions(projectId: number, reviewId: number): Promise<ReviewDecision[]> {
    const response = await apiClient.get<ReviewDecision[]>(
      `/api/platform/projects/${projectId}/reviews/${reviewId}/decisions`
    );
    return response.data;
  },

  async listProjectReviewComments(
    projectId: number,
    filters?: ReviewCommentFilters
  ): Promise<ReviewComment[]> {
    const path = `/api/platform/projects/${projectId}/review-comments`;
    const response = filters
      ? await apiClient.get<ReviewComment[]>(path, { params: filters })
      : await apiClient.get<ReviewComment[]>(path);
    return response.data;
  },

  async getProjectReviewCommentStatusCounts(
    projectId: number,
    filters?: Omit<ReviewCommentFilters, 'status'>
  ): Promise<ReviewCommentStatusCounts> {
    const path = `/api/platform/projects/${projectId}/review-comments/status-counts`;
    const response = filters
      ? await apiClient.get<ReviewCommentStatusCounts>(path, { params: filters })
      : await apiClient.get<ReviewCommentStatusCounts>(path);
    return response.data;
  },

  async createProjectReviewComment(
    projectId: number,
    payload: ReviewCommentCreatePayload
  ): Promise<ReviewComment> {
    const response = await apiClient.post<ReviewComment>(
      `/api/platform/projects/${projectId}/review-comments`,
      payload
    );
    return response.data;
  },

  async updateProjectReviewComment(
    projectId: number,
    commentId: number,
    payload: ReviewCommentUpdatePayload
  ): Promise<ReviewComment> {
    const response = await apiClient.patch<ReviewComment>(
      `/api/platform/projects/${projectId}/review-comments/${commentId}`,
      payload
    );
    return response.data;
  },

  async resolveProjectReviewComment(projectId: number, commentId: number): Promise<ReviewComment> {
    const response = await apiClient.post<ReviewComment>(
      `/api/platform/projects/${projectId}/review-comments/${commentId}/resolve`
    );
    return response.data;
  },

  async reopenProjectReviewComment(projectId: number, commentId: number): Promise<ReviewComment> {
    const response = await apiClient.post<ReviewComment>(
      `/api/platform/projects/${projectId}/review-comments/${commentId}/reopen`
    );
    return response.data;
  },

  async dismissProjectReviewComment(projectId: number, commentId: number): Promise<ReviewComment> {
    const response = await apiClient.post<ReviewComment>(
      `/api/platform/projects/${projectId}/review-comments/${commentId}/dismiss`
    );
    return response.data;
  },

  async checkHealth(): Promise<boolean> {
    const response = await apiClient.get('/api/platform/health');
    return response.data.status === 'healthy';
  },
};
